"use client";

import { useState, useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Settings, LogOut, CreditCard, AlertTriangle, Gauge, Cookie, Gift } from "lucide-react";

import { trackEvent, captureEvent } from "@/src/helpers/trackEvent";
import { useEditorContext } from "@/src/contexts/editorContext";
import { COOKIE_PREFERENCES_OPEN_EVENT } from "@/src/components/CookieConsent";

import { Sheet, SheetContent, SheetTrigger } from "@/src/components/ui/sheet";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/src/components/ui/accordion";
import ManageSubscriptionContent from "@/src/components/Settings/ManageSubscriptionContent";
import UsageContent from "@/src/components/Settings/UsageContent";
import ReferralContent from "@/src/components/Settings/ReferralContent";
import { SectionLabel } from "@/src/components/Settings/PreferenceSections";
import { DeleteAccountButton } from "@/src/components/Settings/DeleteAccountButton";
import { Dialog, DialogContent } from "@/src/components/ui/dialog";
import SettingsSheetButton from "./SettingsSheetButton";
import { KeyboardShortcutsContent } from "./KeyboardShortcutsDialog";
import { ROLES } from "@/src/lib/constants";
import { IcoPlanet } from "@/src/components/Editor/QuarkleIcons";

const LoginButton = () => {
  const { loginWithRedirect } = useAuth0();

  return (
    <SettingsSheetButton
      icon={<LogOut className="h-3.5 w-3.5" style={{ transform: "rotate(180deg)" }} />}
      label="Log in"
      onClick={() => {
        trackEvent("Sign Up", { "Signup Type": "Referral" });
        loginWithRedirect();
      }}
    />
  );
};

// Helper: first letter of the email's local part, uppercase.
export const getUserInitials = (email) => {
  if (!email) return "U";
  const name = email.split("@")[0];
  return (name.charAt(0) || "U").toUpperCase();
};

// Branded pink circle with the email's initial — plain div, no Radix Avatar context
// gating (which can leave the fallback blank when no AvatarImage is rendered).
function InitialAvatar({ email, size = 30, fontSize = 14 }) {
  return (
    <div
      className="flex flex-shrink-0 items-center justify-center rounded-full"
      style={{
        width: size,
        height: size,
        border: "1px solid var(--qk-ink)",
        background: "var(--qk-pink-deep)",
        color: "var(--qk-on-pink)",
        fontFamily: "'Gambarino', serif",
        fontStyle: "italic",
        fontSize,
        fontWeight: 500,
        lineHeight: 1,
      }}
    >
      {getUserInitials(email)}
    </div>
  );
}

// Helper function to truncate email for display
const truncateEmail = (email, maxLength = 32) => {
  if (!email || email.length <= maxLength) return email;

  const [localPart, domain] = email.split("@");
  if (!domain) return email;

  if (localPart.length > maxLength - 10) {
    return `${localPart.substring(0, maxLength - 10)}...@${domain}`;
  }

  const availableSpace = maxLength - localPart.length - 1;
  if (domain.length > availableSpace) {
    return `${localPart}@${domain.substring(0, availableSpace - 3)}...`;
  }

  return email;
};

// Crown shared by every tier state; inherits colour from the badge it sits in.
function CrownIcon({ color }) {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 8l3 8h12l3-8-5 3-4-6-4 6z" />
      <path d="M6 20h12" />
    </svg>
  );
}

// Subscription tier, shown on the profile card. This used to live in the editor
// navbar; it moved here so the navbar slot could carry the referral entry point.
// Pro reads as solid lime rather than another outline pill — on the navy card an
// outline-on-outline badge disappeared into the chrome around it.
function TierBadge({ onClick }) {
  const { subscription } = useEditorContext();
  const isTrial =
    subscription?.subscription_type === "pro" && subscription?.stripe_subscription_status === "trialing";
  const isPro = subscription?.subscription_type === "pro";

  const daysRemaining = useMemo(() => {
    if (!isTrial || !subscription?.stripe_current_period_end) return 0;
    const diff = new Date(subscription.stripe_current_period_end) - new Date();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }, [isTrial, subscription?.stripe_current_period_end]);

  const label = isTrial ? `${daysRemaining}d trial` : isPro ? "pro" : "go pro";

  // Solid lime for paid, lime outline for trial, plain outline CTA for basic.
  const tone = isTrial
    ? { background: "transparent", borderColor: "var(--qk-lime-ink)", color: "var(--qk-lime-ink)" }
    : isPro
      ? { background: "var(--qk-lime)", borderColor: "var(--qk-lime)", color: "var(--qk-on-lime)" }
      : {};
  const iconColor = isPro && !isTrial ? "var(--qk-on-lime)" : "var(--qk-lime-ink)";

  const handleClick = () => {
    captureEvent(isPro ? "subscription_badge_clicked" : "upgrade_cta_clicked", {
      surface: "settings_profile",
      plan: subscription?.subscription_type || "basic",
    });
    onClick?.();
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className="qk-pill shrink-0"
      style={{ minHeight: 28, padding: "0 10px", fontSize: 10, ...tone }}
      aria-label={isPro ? "Manage your Quarkle Pro subscription" : "Upgrade to Quarkle Pro"}
    >
      <span className="flex items-center gap-1.5">
        <CrownIcon color={iconColor} />
        <span>{label}</span>
      </span>
    </button>
  );
}

// User card with pink-deep first-initial avatar and name/email stack.
const UserInfo = ({ setSelected }) => {
  const { user } = useAuth0();

  if (!user) return null;

  return (
    <div
      className="qk-hard-shadow-sm relative flex items-center gap-3 rounded-xl p-3.5"
      style={{
        background: "var(--qk-paper-3)",
        border: "1px solid var(--qk-rule)",
      }}
    >
      <InitialAvatar email={user.email} size={44} fontSize={18} />
      <div className="flex min-w-0 flex-1 flex-col">
        <p
          className="qk-display truncate"
          style={{ fontSize: 18, fontStyle: "italic", color: "var(--qk-ink)", fontWeight: 400 }}
        >
          {user.name || truncateEmail(user.email)}
        </p>
        <p
          className="qk-mono truncate"
          style={{ fontSize: 11, color: "var(--qk-muted)", letterSpacing: "0.02em" }}
        >
          {user.email}
        </p>
      </div>
      <TierBadge onClick={() => setSelected?.("manageSubscription")} />
    </div>
  );
};

function HeaderMark() {
  return (
    <div className="mb-5 mt-1 flex items-center justify-center gap-2">
      <IcoPlanet size={22} color="var(--qk-ink)" />
      <span
        style={{
          fontFamily: "'Gambarino', serif",
          fontSize: 20,
          fontStyle: "italic",
          color: "var(--qk-ink)",
          letterSpacing: "-0.01em",
        }}
      >
        your quarkle
      </span>
    </div>
  );
}

// The account sheet: who you are, and the account rows. Manuscript actions
// live on the editor rail; preferences live on the companion panel's gear.
function DefaultSettingsContent({ setSelected }) {
  const { isAuthenticated, logout } = useAuth0();
  const { role } = useEditorContext();
  const [isShortcutsOpen, setIsShortcutsOpen] = useState(false);

  return (
    <div className="flex h-full flex-col">
      <HeaderMark />

      {role === ROLES.PUBLIC_VIEWER ? (
        <div className="flex flex-col gap-1 px-2">
          <LoginButton />
        </div>
      ) : (
        <div className="flex h-full flex-col">
          {isAuthenticated && <UserInfo setSelected={setSelected} />}

          <SectionLabel>account</SectionLabel>
          <div className="flex flex-col px-1">
            <SettingsSheetButton
              icon={<Gift className="h-3.5 w-3.5" />}
              label="Refer & Earn"
              onClick={() => {
                captureEvent("referral_panel_opened", { surface: "settings_menu" });
                setSelected("referral");
              }}
            />
            <SettingsSheetButton
              icon={<Gauge className="h-3.5 w-3.5" />}
              label="Usage"
              onClick={() => setSelected("usage")}
            />
            <SettingsSheetButton
              icon={<CreditCard className="h-3.5 w-3.5" />}
              label="Manage Subscription"
              onClick={() => setSelected("manageSubscription")}
            />
            <SettingsSheetButton
              icon={<Settings className="h-3.5 w-3.5" />}
              label="Keyboard Shortcuts"
              onClick={() => setIsShortcutsOpen(true)}
            />
            <SettingsSheetButton
              icon={<Cookie className="h-3.5 w-3.5" />}
              label="Cookie Preferences"
              onClick={() => window.dispatchEvent(new CustomEvent(COOKIE_PREFERENCES_OPEN_EVENT))}
            />
          </div>

          <div className="flex-1" />

          {/* Bottom stack: Log Out (order-1), then collapsed Danger accordion (order-2). */}
          {isAuthenticated && (
            <div className="mt-3 flex flex-col gap-1 pb-2">
              <div className="order-1 px-1">
                <SettingsSheetButton
                  icon={<LogOut className="h-3.5 w-3.5" />}
                  label="Log Out"
                  onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
                />
              </div>

              <div className="order-2 px-1">
                <Accordion type="single" collapsible>
                  <AccordionItem value="danger" className="border-0">
                    <AccordionTrigger
                      className="mx-3 flex items-center gap-2 rounded-lg px-3 py-2 hover:no-underline"
                      style={{
                        borderTop: "1px dashed var(--qk-clay)",
                        color: "var(--qk-clay)",
                      }}
                    >
                      <span className="flex flex-1 items-center gap-2">
                        <AlertTriangle className="h-3 w-3" />
                        <span className="qk-caps">danger</span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="pb-2 pt-2">
                      <DeleteAccountButton />
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </div>
          )}
        </div>
      )}

      <Dialog open={isShortcutsOpen} onOpenChange={setIsShortcutsOpen}>
        <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-[500px]">
          <KeyboardShortcutsContent />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SheetContentBody({ selected, setSelected }) {
  return (
    <SheetContent selected={selected} setSelected={setSelected}>
      {selected === null && <DefaultSettingsContent setSelected={setSelected} />}
      {selected === "manageSubscription" && <ManageSubscriptionContent setSelected={setSelected} />}
      {selected === "usage" && <UsageContent setSelected={setSelected} />}
      {selected === "referral" && <ReferralContent />}
    </SheetContent>
  );
}

export function SettingsSheets() {
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const { user, isAuthenticated } = useAuth0();

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger className="qk-icon-button qk-button-round sm:mr-2" aria-label="Open account settings">
        {isAuthenticated && user ? (
          <InitialAvatar email={user.email} size={30} fontSize={14} />
        ) : (
          <i className="pi pi-bars text-xl" style={{ color: "var(--qk-ink)" }}></i>
        )}
      </SheetTrigger>
      <SheetContentBody selected={selected} setSelected={setSelected} />
    </Sheet>
  );
}

// Editor-navbar entry point for the referral program. This slot used to hold the
// tier pill ("using pro"); that moved onto the settings profile card, freeing the
// most visible spot in the app for the thing we actually want people to click.
export function SettingsSheetTriggerReferral() {
  const [selected, setSelected] = useState("referral");

  return (
    <Sheet
      onOpenChange={(open) => {
        if (!open) return;
        setSelected("referral");
        captureEvent("referral_panel_opened", { surface: "editor_navbar" });
      }}
    >
      <SheetTrigger className="qk-pill qk-pill-pro qk-referral-pill" aria-label="Refer a friend and earn credit">
        <span className="flex items-center gap-1.5">
          <Gift className="h-3 w-3" style={{ color: "var(--qk-lime)" }} />
          <span className="hidden sm:inline">refer &amp; earn</span>
        </span>
      </SheetTrigger>
      <SheetContentBody selected={selected} setSelected={setSelected} />
    </Sheet>
  );
}
